import m from "mithril";
import Requester from "../../shared/request";
import Gateway from "../../shared/app_settings";
import Toasts from "../../shared/toasts";
import i18n from "../../shared/i18n/i18n";
import ModalManager from "../../shared/modal";
import FormUtilities from "../../shared/utilities/form_utilities";
import DateInput from "../../shared/components/component_date_input";
import Modal_Confirm from "../../shared/components/modal_confirm";
import Component_Signature_Editor from "./component_signature_editor";
import DateUtilities from "../../shared/utilities/date_utilities";
import DropdownField from "../../shared/components/component_dropdown_field";
import sanitizeHtml from "sanitize-html";
import TextUtilities from "../../shared/utilities/text_utilities";

let originalUnit, unitEnrolmentId, enrolmentId, formUnit, exitCallback, saving, loading, dirty, modalId;

function fieldHasOptions(fieldType) {
    return fieldType === "checkbox" || fieldType === "radio" || fieldType.includes("dropdown");
}

function loadForm() {
    loading = true;
    formUnit = null;
    Requester.get(Gateway.public + "units/form/" + unitEnrolmentId)
        .then(function (result) {
            formUnit = result;
        })
        .catch(function (error) {
            Toasts.add("error", i18n.t("error"), i18n.t(error.response.properties.errorCode));
        })
        .then(function () {
            loading = false;
        });
}

function loadFormFromAdmin() {
    loading = true;
    formUnit = null;
    Requester.get(Gateway.admin + "units/form/" + unitEnrolmentId)
        .then(function (result) {
            formUnit = result;
        })
        .catch(function (error) {
            Toasts.add("error", i18n.t("error"), i18n.t(error.response.properties.errorCode));
        })
        .then(function () {
            loading = false;
        });
}

function calculateRows(value) {
    const maxLength = 65;
    let lines = value?.split("\n");
    let rowCount = 1;

    lines?.forEach(line => {
        const additionalRows = Math.ceil(line.length / maxLength);
        rowCount += additionalRows;
    });

    return rowCount > 1 ? rowCount : 1; // Ensure at least 1 row
}

function saveForm() {
    if (formHasValidationErrors()) {
        displayFieldRequiredErrors = true;
        return;
    }

    if (FormUtilities.validateFields("#form .form-input")) {
        saving = true;

        let fields = [];
        for (let i = 0; i < formUnit.sections.length; i++) {
            for (let x = 0; x < formUnit.sections[i].fields.length; x++) {
                let field = formUnit.sections[i].fields[x];

                // Every field sends a consistent value shape: an array of { id, value }.
                // For option-based fields (radio/checkbox/dropdown) the id is the selected
                // option's id, so the backend can tell which specific option was chosen -
                // option values can be duplicated across the form, and
                // prerequisites are keyed on option ids. Fields without options (textfield,
                // date, etc.) have no id, so it is left blank.
                let value;
                if (fieldHasOptions(field.type)) {
                    value = field.options
                        .filter(o => o.selected)
                        .map(o => ({ id: o.id, value: o.value }));
                } else {
                    value = [{ id: "", value: field.value || "" }];
                }

                fields.push({
                    fieldEnrolmentId: field.fieldEnrolmentId,
                    id: field.id,
                    name: field.name,
                    type: field.type,
                    value: value
                });
            }
        }

        let data = {
            id: unitEnrolmentId,
            unitId: formUnit.id,
            unitName: formUnit.name,
            fields: fields
        };

        Requester.post(Gateway.public + "units/form?enrolmentId=" + enrolmentId, data)
            .then(function (result) {
                exitCallback();
                ModalManager.close(modalId);
                Toasts.add("success", i18n.t("progress_saved"));
            })
            .catch(function (error) {
                console.error("Failed to submit form", error);
                const errorCode = error && error.response && error.response.properties && error.response.properties.errorCode;
                Toasts.add("error", i18n.t("error"), errorCode ? i18n.t(errorCode) : i18n.t("something_went_wrong"));
            })
            .finally(function () {
                saving = false;
            });
    }
}

function onCancel() {
    if (dirty) {
        promptToCancel();
    } else {
        cancel();
    }
}

function promptToCancel() {
    const attributes = {
        confirmCallback: null,
        cancelCallback: cancel,
        messageText: i18n.t("unsaved_changes_message"),
        confirmButtonLabel: i18n.t("continue_editing"),
        cancelButtonLabel: i18n.t("discard"),
        confirmButtonClasses: "button",
        cancelButtonClasses: "button.btn-error"
    };
    ModalManager.open(i18n.t("unsaved_changes"), [], Modal_Confirm, attributes)
}

function cancel() {
    exitCallback();
    ModalManager.close(modalId);
}

function checkSectionDependencies(section) {
    let ids = [];
    let reachedCurrent = false;

    if (!section.prereqOptionIds || section.prereqOptionIds.length === 0)
        return true;
    else {
        for (let i = 0; i < formUnit.sections.length; i++) {
            if (formUnit.sections[i].id === section.id)
                reachedCurrent = true;

            if (!reachedCurrent) {
                for (let x = 0; x < formUnit.sections[i].fields.length; x++) {
                    if (fieldHasOptions(formUnit.sections[i].fields[x].type)) {
                        for (let z = 0; z < formUnit.sections[i].fields[x].options.length; z++) {
                            if (formUnit.sections[i].fields[x].options[z].selected)
                                ids.push(formUnit.sections[i].fields[x].options[z].id);
                        }
                    }
                }
            }
        }
    }

    if (ids.length === 0)
        return false;

    for (let i = 0; i < section.prereqOptionIds.length; i++) {
        if (ids.indexOf(section.prereqOptionIds[i]) === -1) {
            return false;
        }
    }

    return true;
}

function checkFieldDependencies(field) {
    let ids = [];
    let reachedCurrent = false;

    if (!field.prereqOptionIds || field.prereqOptionIds.length === 0)
        return true;
    else {
        for (let i = 0; i < formUnit.sections.length; i++) {
            for (let x = 0; x < formUnit.sections[i].fields.length; x++) {
                if (formUnit.sections[i].fields[x].id === field.id)
                    reachedCurrent = true;

                if (!reachedCurrent) {
                    if (fieldHasOptions(formUnit.sections[i].fields[x].type)) {
                        for (let z = 0; z < formUnit.sections[i].fields[x].options.length; z++) {
                            if (formUnit.sections[i].fields[x].options[z].selected)
                                ids.push(formUnit.sections[i].fields[x].options[z].id);
                        }
                    }
                }
            }
        }
    }

    if (ids.length === 0)
        return false;

    for (let i = 0; i < field.prereqOptionIds.length; i++) {
        if (ids.indexOf(field.prereqOptionIds[i]) === -1) {
            return false;
        }
    }

    return true;
}

function isEmptyString(string) {
    return string === null || string?.trim() === '';
}

function requiredFieldIsEmpty(field) {
    if (!field.isRequired) {
        return false;
    }

    if (field.type === "radio" || field.type === "checkbox" || field.type === "dropdownsingle" || field.type === "dropdownmulti") {
        return !field.options.some(o => o.selected);
    } else {
        return isEmptyString(field.value);
    }
}

function fieldIsRequiredMessage(field) {
    let message = "";
    switch (field.type) {
        case "email":
            message = i18n.t("email_not_valid");
            break;
        case "number":
            message = i18n.t("number_not_valid");
            break;
        case "date":
            message = i18n.t("date_not_valid");
            break;
        case "checkbox":
            message = i18n.t("checkbox_not_valid");
            break;
        case "radio":
            message = i18n.t("radio_button_not_valid");
            break;
        case "textfield":
        case "textbox":
        default:
            message = i18n.t("field_mandatory");
            break;
    }

    return message;
}

function getTextErrorElem(message, field) {
    addFieldValidationErrors(message, field);
    return m(".text-error padding", { style: "font-size: 0.75rem;" }, message)
}

function addFieldValidationErrors(message, field) {
    fieldValidationErrorsDict[field.id] = `${message}`;
    return "";
}

function clearFieldValidationErrors(field) {
    delete fieldValidationErrorsDict[field.id]
    return "";
}

function formHasValidationErrors() {
    return Object.keys(fieldValidationErrorsDict).length > 0;
}

function getFieldClass(field) {
    let c = [];
    if (field.isRequired)
        c.push("required");
    if (field.hasError)
        c.push("has-error");

    return c.join(" ");
}

function onSignatureUpdate(field) {
    dirty = true;
    validateField(field);
}

function validateField(field) {
    if (requiredFieldIsEmpty(field)) {
        if (displayFieldRequiredErrors) {
            return getTextErrorElem(fieldIsRequiredMessage(field), field);
        } else {
            addFieldValidationErrors(fieldIsRequiredMessage(field), field);
        }
    } else if (field.type === "email" && invalidEmailFieldIds.indexOf(field.value) != -1) {
        return getTextErrorElem(i18n.t('email_must_be_valid'), field);
    } else if (field.type === "date" && invalidDateFieldIds.indexOf(field.id) !== -1) {
        return getTextErrorElem(i18n.t('date_not_valid_format', { format: DateUtilities.getLocaleDateString(i18n.locale) }, 'Enter a valid date in {{format}} format.'), field);
    } else {
        clearFieldValidationErrors(field);
    }
}

let invalidEmailFieldIds;
let isEmailValid;
let invalidDateFieldIds;

let displayFieldRequiredErrors;
var fieldValidationErrorsDict;

function renderFieldInput(field, vnode) {
    if (field.type === "textfield") {
        return m("input[type=text].form-input", {
            value: field.value,
            placeholder: field.info,
            oninput: function () { dirty = true; field.value = this.value; }
        });
    }
    if (field.type === "textbox") {
        return m("textarea.form-input", {
            value: field.value,
            placeholder: field.info,
            rows: calculateRows(field.value),
            oninput: function () { dirty = true; field.value = this.value; }
        });
    }
    if (field.type === "email") {
        return m("input[type=email].form-input", {
            value: field.value,
            placeholder: i18n.t("enter_email"),
            oninput: function () {
                dirty = true;
                field.value = this.value;
                FormUtilities.validateEmail(this.value, !field.isRequired) ? invalidEmailFieldIds.splice(invalidEmailFieldIds.indexOf(this.value)) : invalidEmailFieldIds.push(this.value);
            }
        });
    }
    if (field.type === "number") {
        return m("input[type=number].form-input", {
            value: field.value,
            placeholder: field.info,
            oninput: function () { dirty = true; field.value = this.value; }
        });
    }
    if (field.type === "date") {
        return m(DateInput, {
            field,
            i18n,
            invalidDateFieldIds,
            dirtyFlag: function (v) { dirty = v; }
        });
    }
    if (field.type === "checkbox") {
        return m(".form-input checkbox-group",
            field.options.map(function (option) {
                return m("label", [
                    m("input[type=checkbox]", {
                        name: field.id,
                        oncreate: function (e) { e.dom.checked = option.selected; },
                        onchange: function () { option.selected = this.checked; }
                    }),
                    option.value
                ]);
            })
        );
    }
    if (field.type === "radio") {
        return m(".form-input radio-group",
            field.options.map(function (option) {
                return m("label", [
                    m("input[type=radio]", {
                        name: field.id,
                        oncreate: function (e) { e.dom.checked = option.selected; },
                        onchange: function () {
                            for (let i = 0; i < field.options.length; i++) {
                                field.options[i].selected = false;
                            }
                            option.selected = this.checked;
                        }
                    }),
                    option.value
                ]);
            })
        );
    }
    if (field.type === "dropdownsingle" || field.type === "dropdownmulti") {
        return m(DropdownField, {
            field: field,
            readOnly: vnode.attrs.readOnly,
            onSelect: function () { dirty = true; validateField(field); }
        });
    }
    if (field.type === "signature") {
        return m(".form-input",
            m(Component_Signature_Editor, {id: field.id, readOnly: vnode.attrs.readOnly, field: field, updateCallback: onSignatureUpdate})
        );
    }
    return "";
}

const Modal_Form = {
    oninit: function (vnode) {
        enrolmentId = vnode.attrs.enrolmentId;
        originalUnit = vnode.attrs.unit;
        unitEnrolmentId = vnode.attrs.unitEnrolmentId;
        if(!!originalUnit) {
            unitEnrolmentId = originalUnit.enrolment.id;
        }
        exitCallback = vnode.attrs.callback;
        invalidEmailFieldIds = [];
        isEmailValid = invalidEmailFieldIds.length === 0;
        invalidDateFieldIds = [];
        displayFieldRequiredErrors = false;
        fieldValidationErrorsDict = {};
        if(vnode.attrs.readOnly && vnode.attrs.loadFromAdmin) {
            loadFormFromAdmin();
        } else {
            loadForm();
        }
        dirty = false;
    },
    view: function (vnode) {
        return [
            formUnit ? [
                TextUtilities.hasVisibleText(formUnit.instructions) ? m(".instructions.mb-2", [
                    m("div", m.trust(sanitizeHtml(formUnit.instructions || "", {allowedTags: sanitizeHtml.defaults.allowedTags, allowedAttributes: {"*": ["style", "class"], "a": ["href", "target"]}})))
                ]) : "",
                m(".form-sections form full-width", { class: vnode.attrs.readOnly ? "disable-fields" : "" }, [
                    formUnit.sections.map(function (section) {
                        return checkSectionDependencies(section) ?
                            m(".form-section mb-2 full-width", [
                                m(".tile.tile-centered.m-0", [
                                    m(".tile-content", [
                                        m(".form-label.text-bold.richtext", m.trust(sanitizeHtml(section.name || "", {allowedTags: sanitizeHtml.defaults.allowedTags, allowedAttributes: {"*": ["style", "class"], "a": ["href", "target"]}}))),
                                        section.info ? m(".form-subtitle.richtext.mb-3", m.trust(sanitizeHtml(section.info || "", {allowedTags: sanitizeHtml.defaults.allowedTags, allowedAttributes: {"*": ["style", "class"], "a": ["href", "target"]}}))) : ""
                                    ])
                                ]),
                                section.fields.length > 0 ? m(".form form-section-fields full-width", [
                                    section.fields.map(function (field) {
                                        return checkFieldDependencies(field) ?
                                            m(".form-section", { class: getFieldClass(field) }, [
                                                m(".form-label.richtext", m.trust(sanitizeHtml(field.name || "", {allowedTags: sanitizeHtml.defaults.allowedTags, allowedAttributes: {"*": ["style", "class"], "a": ["href", "target"]}}))),
                                                renderFieldInput(field, vnode)
                                                , validateField(field)
                                            ])
                                            : ""
                                    })
                                ]) : ""
                            ])
                            : ""
                    })
                ]),
                m(".form-buttons", [
                    !vnode.attrs.readOnly ? [
                        !saving && isEmailValid ? m("button.btn-focus", { onclick: saveForm }, i18n.t("save")) : m("button.btn-text no-click", [i18n.t("save"), m(".loading-dots")]),
                        !saving ? m("button", { onclick: onCancel }, i18n.t("cancel")) : ""
                    ] : ""
                ])
            ] : m(".text-gray", [i18n.t("loading"), m(".loading-dots")]),
            formHasValidationErrors() && displayFieldRequiredErrors ? m(".text-error padding", { style: "font-size: 0.75rem;" }, i18n.t("complete_required_fields")) : ""
        ];
    },
    setModalId: function(id) {
        modalId = id;
    }
};

export default Modal_Form;
